import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

export type ExportFormat = 'csv' | 'xlsx';

export interface FieldMapping {
  field: string;
  label: string;
  required?: boolean;
  transform?: (value: any, row: any) => any;
}

export interface ImportResult {
  success: number;
  failed: number;
  errors: string[];
  importedRows: any[];
}

export interface ParsedFile {
  headers: string[];
  rows: Record<string, any>[];
}

/**
 * Parse a CSV or Excel file into headers and rows
 */
export async function parseFile(file: File): Promise<ParsedFile> {
  const ext = file.name.split('.').pop()?.toLowerCase();

  if (ext === 'csv' || ext === 'txt') {
    return parseCSV(file);
  } else if (ext === 'xlsx' || ext === 'xls' || ext === 'ods') {
    return parseExcel(file);
  } else {
    throw new Error(`Unsupported file format: ${ext}`);
  }
}

function parseCSV(file: File): Promise<ParsedFile> {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const headers = results.meta.fields || [];
        const rows = results.data as Record<string, any>[];
        resolve({ headers, rows });
      },
      error: (error) => reject(error),
    });
  });
}

async function parseExcel(file: File): Promise<ParsedFile> {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array' });
  const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
  const json = XLSX.utils.sheet_to_json<Record<string, any>>(firstSheet, { defval: '' });
  
  const headers = json.length > 0 ? Object.keys(json[0]) : [];
  return { headers, rows: json };
}

/**
 * Auto-map source headers to target fields based on label similarity
 */
export function autoMapFields(
  sourceHeaders: string[],
  targetMappings: FieldMapping[]
): Record<string, string> {
  const mapping: Record<string, string> = {};
  
  for (const target of targetMappings) {
    const targetLower = target.label.toLowerCase();
    
    // Exact match
    const exactMatch = sourceHeaders.find(
      h => h.toLowerCase() === targetLower
    );
    if (exactMatch) {
      mapping[target.field] = exactMatch;
      continue;
    }
    
    // Partial match (target label contains header or vice versa)
    const partialMatch = sourceHeaders.find(
      h => h.toLowerCase().includes(targetLower) || targetLower.includes(h.toLowerCase())
    );
    if (partialMatch) {
      mapping[target.field] = partialMatch;
      continue;
    }
    
    // Field name match
    const fieldMatch = sourceHeaders.find(
      h => h.toLowerCase() === target.field.toLowerCase()
    );
    if (fieldMatch) {
      mapping[target.field] = fieldMatch;
    }
  }
  
  return mapping;
}

/**
 * Validate parsed rows against field mappings
 */
export function validateRows(
  rows: Record<string, any>[],
  fieldMapping: Record<string, string>,
  targetMappings: FieldMapping[]
): { valid: Record<string, any>[]; errors: string[] } {
  const valid: Record<string, any>[] = [];
  const errors: string[] = [];
  
  rows.forEach((row, index) => {
    const rowData: Record<string, any> = {};
    let hasError = false;
    
    for (const target of targetMappings) {
      const sourceColumn = fieldMapping[target.field];
      
      if (!sourceColumn && target.required) {
        errors.push(`Row ${index + 2}: Missing required field "${target.label}"`);
        hasError = true;
        continue;
      }
      
      let value = sourceColumn ? row[sourceColumn] : '';
      
      if (target.transform) {
        value = target.transform(value, row);
      }
      
      rowData[target.field] = value;
    }
    
    if (!hasError) {
      valid.push(rowData);
    }
  });
  
  return { valid, errors };
}

/**
 * Export data to CSV or Excel format
 */
export function exportData(
  data: Record<string, any>[],
  mappings: FieldMapping[],
  filename: string,
  format: ExportFormat = 'csv'
): void {
  const headers = mappings.map(m => m.label);
  
  const rows = data.map(item => {
    const row: Record<string, any> = {};
    for (const mapping of mappings) {
      let value = item[mapping.field];
      if (mapping.transform) {
        value = mapping.transform(value, item);
      }
      row[mapping.label] = value ?? '';
    }
    return row;
  });
  
  if (format === 'csv') {
    exportCSV(rows, headers, filename);
  } else {
    exportExcel(rows, headers, filename);
  }
}

function exportCSV(
  rows: Record<string, any>[],
  headers: string[],
  filename: string
): void {
  const csv = Papa.unparse({
    fields: headers,
    data: rows.map(r => headers.map(h => r[h] ?? '')),
  });
  
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
  saveAs(blob, `${filename}.csv`);
}

function exportExcel(
  rows: Record<string, any>[],
  headers: string[],
  filename: string
): void {
  const worksheet = XLSX.utils.json_to_sheet(rows, { header: headers });
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');
  
  const buffer = XLSX.write(workbook, { type: 'array', bookType: 'xlsx' });
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  saveAs(blob, `${filename}.xlsx`);
}

/**
 * Generate a template file for import
 */
export function downloadTemplate(
  mappings: FieldMapping[],
  filename: string,
  format: ExportFormat = 'csv'
): void {
  const sampleRow: Record<string, any> = {};
  for (const m of mappings) {
    sampleRow[m.label] = '';
  }
  
  exportData([sampleRow], mappings, filename, format);
}

/**
 * Clean and normalize string values from import
 */
export function cleanString(value: any): string {
  if (value === null || value === undefined) return '';
  return String(value).trim();
}

/**
 * Parse boolean from various string representations
 */
export function parseBoolean(value: any): boolean {
  if (typeof value === 'boolean') return value;
  const str = String(value).toLowerCase().trim();
  return ['true', '1', 'yes', 'igen', 'aktív', 'active'].includes(str);
}

/**
 * Parse number from string, handling Hungarian decimal separators
 */
export function parseNumber(value: any): number {
  if (typeof value === 'number') return value;
  if (!value) return 0;
  const str = String(value).replace(/\s/g, '').replace(',', '.');
  return parseFloat(str) || 0;
}
